import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function BillingSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("polling");
  const [plan, setPlan] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get("session_id");
    if (!sid) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const r = await api.get(`/billing/status/${sid}`);
        setPlan(r.data.plan);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          await checkAuth();
          return;
        }
        if (r.data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts.current >= 8) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [location.search, checkAuth]);

  return (
    <div className="p-6 sm:p-8 lg:p-12 max-w-xl">
      <div className="nb-card p-8 text-center">
        {status === "polling" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin" strokeWidth={2.5}/>
            <h1 className="font-display text-3xl font-black mt-3">Checking your payment…</h1>
            <p className="text-sm text-slate-600 mt-2">This usually takes a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto" strokeWidth={2.5}/>
            <h1 className="font-display text-3xl font-black mt-3">You're {plan === "pro_plus" ? "Pro Plus" : "Pro"}!</h1>
            <p className="text-sm text-slate-600 mt-2">Audio/video uploads, AI assignment drafts and more storage are unlocked.</p>
            <div className="flex gap-3 justify-center mt-6">
              <button onClick={() => navigate("/dashboard")} className="nb-btn-primary">Go to dashboard</button>
              <Link to="/materials" className="nb-btn-secondary">Upload something</Link>
            </div>
          </>
        )}
        {status === "expired" && (
          <>
            <XCircle className="w-12 h-12 mx-auto" strokeWidth={2.5}/>
            <h1 className="font-display text-3xl font-black mt-3">Checkout expired</h1>
            <p className="text-sm text-slate-600 mt-2">No charge was made.</p>
            <Link to="/pricing" className="nb-btn-primary mt-6 inline-block">Try again</Link>
          </>
        )}
        {(status === "timeout" || status === "error") && (
          <>
            <XCircle className="w-12 h-12 mx-auto" strokeWidth={2.5}/>
            <h1 className="font-display text-3xl font-black mt-3">Couldn't confirm yet</h1>
            <p className="text-sm text-slate-600 mt-2">If you completed payment, your plan will update automatically. Check your dashboard in a minute.</p>
            <Link to="/dashboard" className="nb-btn-primary mt-6 inline-block">Back to dashboard</Link>
          </>
        )}
      </div>
    </div>
  );
}
